import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.wineryfinderapp.com';
const SITE_NAME = 'Winery Finder App';
const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1701623785014-181cda1bcc37?w=1200';

/**
 * SEO Component with structured data for Google Search Console
 * 
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Meta description (max 160 chars)
 * @param {string} props.path - Current page path (e.g., '/blog')
 * @param {string} props.image - OG image URL
 * @param {string} props.type - Page type: 'website', 'article', 'product', 'local_business'
 * @param {Object} props.article - Article data for blog posts
 * @param {Object} props.product - Product data for audio tours
 * @param {Object} props.breadcrumbs - Breadcrumb data
 * @param {boolean} props.noindex - Whether to noindex the page
 */
const SEO = ({
  title,
  description,
  path = '',
  image = DEFAULT_IMAGE,
  type = 'website',
  article = null,
  product = null,
  breadcrumbs = null,
  noindex = false,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - #1 Downloaded Wine Tasting App`;
  const canonicalUrl = `${SITE_URL}${path}`;
  const fullDescription = description || 
    'Discover great wineries in Napa Valley & Sonoma. FREE app finds wine tasting coupons, deals & discounts to 140+ wineries. Plan your perfect wine country trip!';

  // Organization Schema (appears on every page)
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/images/logo.png`,
    description: 'The #1 downloaded wine tasting app for Napa Valley and Sonoma.',
    sameAs: [
      'https://www.facebook.com/wineryfinderapp',
      'https://www.instagram.com/wineryfinderapp',
      'https://twitter.com/wineryfinderapp'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'support@wineryfinderapp.com'
    }
  };

  // Mobile App Schema
  const mobileAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    name: SITE_NAME,
    description: 'Find great wineries in Napa Valley & Sonoma with GPS-enabled search, wine tasting deals, and trip planning.',
    applicationCategory: 'TravelApplication',
    operatingSystem: 'iOS, Android',
    downloadUrl: [
      'https://apps.apple.com/us/app/napa-valley-winery-finder-real/id419570092',
      'https://play.google.com/store/apps/details?id=com.shoutem.n75202&hl=en'
    ],
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD'
    }
    // No aggregateRating: rich-result review markup must match real,
    // verifiable ratings (the App Store figure changes; don't hard-code it).
  };

  // Article Schema for blog posts
  const articleSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt || description,
    image: article.image || image,
    datePublished: article.publishedAt,
    dateModified: article.updatedAt || article.publishedAt,
    author: {
      '@type': 'Organization',
      name: SITE_NAME
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/images/logo.png`
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl
    }
  } : null;

  // Product Schema for audio tours
  const productSchema = product ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.image || image,
    offers: {
      '@type': 'Offer',
      price: (product.priceCents / 100).toFixed(2),
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock'
    },
    // Only real ratings with a real review count — never a default count.
    aggregateRating: product.rating && product.reviewCount ? {
      '@type': 'AggregateRating',
      ratingValue: product.rating,
      reviewCount: product.reviewCount
    } : undefined
  } : null;

  // Breadcrumb Schema
  const breadcrumbSchema = breadcrumbs ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: `${SITE_URL}${crumb.path}`
    }))
  } : null;

  // Local Business Schema for winery pages
  const localBusinessSchema = type === 'local_business' ? {
    '@context': 'https://schema.org',
    '@type': 'Winery',
    name: title,
    description: description,
    image: image,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Napa Valley',
      addressRegion: 'CA',
      addressCountry: 'US'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '38.2975',
      longitude: '-122.2869'
    }
  } : null;

  // FAQ Schema (can be passed for specific pages)
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/wineries?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={fullDescription} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
      <meta name="googlebot" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type === 'article' ? 'article' : 'website'} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={image} />
      
      {/* Article specific */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedAt} />
          <meta property="article:modified_time" content={article.updatedAt || article.publishedAt} />
          <meta property="article:author" content={SITE_NAME} />
          <meta property="article:section" content={article.category || 'Wine'} />
        </>
      )}
      
      {/* GEO Tags for Local SEO */}
      <meta name="geo.region" content="US-CA" />
      <meta name="geo.placename" content="Napa Valley, California" />
      <meta name="geo.position" content="38.2975;-122.2869" />
      <meta name="ICBM" content="38.2975, -122.2869" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      
      {path === '/' && (
        <script type="application/ld+json">
          {JSON.stringify(mobileAppSchema)}
        </script>
      )}
      
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
      
      {productSchema && (
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      )}
      
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      
      {localBusinessSchema && (
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
